import { useEffect, useRef, useState } from 'react'
import { FiCheck, FiChevronDown, FiChevronUp, FiPlus } from 'react-icons/fi'
import {
	Platform,
	AccountLinkType,
	GoogleOAuthAccountLinkDTO,
	FacebookAccountLinkDTO,
	BingAccountLinkDTO
} from './types'

interface AuthorizationDropdownProps {
	platform: Platform
	availableAccountLinks: AccountLinkType[]
	selectedAccountLinkId: string | null
	accountLink: AccountLinkType | null
	loading: boolean
	onChange: (link: AccountLinkType) => void
	onCreateNewAuthorization: () => void
	getConnectedAccountInfo: () => string | null
}

export const AuthorizationDropdown = ({
	platform,
	availableAccountLinks,
	selectedAccountLinkId,
	accountLink,
	loading,
	onChange,
	onCreateNewAuthorization,
	getConnectedAccountInfo
}: AuthorizationDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const dropdownRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const getEmail = (link: AccountLinkType) => {
		if (platform === 'GADS') {
			return (link as GoogleOAuthAccountLinkDTO).name
		} else if (platform === 'FADS') {
			return (link as FacebookAccountLinkDTO).email
		} else {
			return (link as BingAccountLinkDTO).email
		}
	}

	const getOptionLabel = (link: AccountLinkType | null) => {
		if (!link) return getConnectedAccountInfo() || 'Select authorization'
		return getEmail(link) || 'Authorized credential'
	}

	const getInitial = (link: AccountLinkType | null) => {
		return getOptionLabel(link).trim().charAt(0).toUpperCase() || 'N'
	}

	const handleChange = (link: AccountLinkType) => {
		onChange(link)
		setIsOpen(false)
	}

	const handleCreateNewAuthorization = () => {
		setIsOpen(false)
		onCreateNewAuthorization()
	}

	return (
		<div className="relative flex flex-col gap-[6px]" ref={dropdownRef}>
			<button
				type="button"
				disabled={loading}
				onClick={() => setIsOpen((prev) => !prev)}
				className="flex w-full items-center gap-[16px] rounded-[12px] border border-t-checkbox bg-white p-[16px] text-left shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] disabled:cursor-not-allowed disabled:opacity-60"
			>
				<div className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-brand text-[21px] leading-none text-white">
					{getInitial(accountLink)}
				</div>
				<div className="flex min-w-0 flex-1 flex-col gap-[2px]">
					<p className="truncate text-[18px] font-medium leading-[20px] tracking-[0.09px] text-gray-700">
						{getOptionLabel(accountLink)}
					</p>
					<p className="truncate text-[16px] leading-[18px] tracking-[0.002px] text-gray-700">
						{getOptionLabel(accountLink)}
					</p>
				</div>
				<div className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[6px] text-t-default">
					{isOpen ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
				</div>
			</button>

			{isOpen && (
				<div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-[14px] border border-card-border bg-popover p-px shadow-dropShadow">
					{availableAccountLinks.map((link) => {
						const isSelected = link.id === selectedAccountLinkId
						return (
							<button
								key={link.id}
								type="button"
								onClick={() => handleChange(link)}
								className="flex h-[76px] w-full items-center gap-[12px] p-[16px] text-left hover:bg-popover-hover"
							>
								<div className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-brand text-[21px] leading-none text-white">
									{getInitial(link)}
								</div>
								<div className="min-w-0 flex-1">
									<p className="truncate text-[18px] font-medium leading-[20px] tracking-[0.09px] text-gray-700">
										{getOptionLabel(link)}
									</p>
									<p className="mt-[2px] truncate text-[16px] leading-[18px] tracking-[0.002px] text-gray-700">
										{getOptionLabel(link)}
									</p>
								</div>
								{isSelected && (
									<FiCheck className="h-5 w-5 shrink-0 text-t-default" />
								)}
							</button>
						)
					})}
					<div className="h-px w-full bg-card-border" />
					<button
						type="button"
						onClick={handleCreateNewAuthorization}
						className="flex h-[72px] w-full items-center gap-[12px] p-[16px] text-left hover:bg-popover-hover"
					>
						<div className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-tint-text">
							<FiPlus size={20} />
						</div>
						<p className="text-[18px] font-medium leading-[20px] tracking-[0.09px] text-gray-700">
							Add another credential
						</p>
					</button>
				</div>
			)}
		</div>
	)
}
